import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface Branch {
  id: number;
  name: string;
  address: string;
  hours: string;
  phone: string;
  mapUrl: string;
}

interface BranchWithCoords extends Branch {
  lat: number;
  lng: number;
}

interface Props {
  branches: Branch[];
  activeBranch: number | null;
  onSelectBranch: (index: number) => void;
  onNearestFound: (index: number) => void;
}

// Coordenadas reales de las 35 sucursales de San7o Pecado
const BRANCH_COORDS: { lat: number; lng: number }[] = [
  { lat: 19.2733291, lng: -99.6851932 },   // 1. San Buenaventura
  { lat: 19.2681464, lng: -99.5768247 },   // 2. Av Tecnológico
  { lat: 19.2727122, lng: -99.6069063 },   // 3. Providencia
  { lat: 19.351889, lng: -99.5959585 },    // 4. Los Sauces
  { lat: 19.2953602, lng: -99.710726 },    // 5. Zamarrero
  { lat: 19.26959, lng: -99.5888845 },     // 6. López Mateos
  { lat: 19.3034499, lng: -99.6342259 },   // 7. Ruta de la Independencia
  { lat: 19.260393, lng: -99.6380116 },    // 8. Ceboruco
  { lat: 19.3564967, lng: -99.5811814 },   // 9. Carr. Toluca - Naucalpan
  { lat: 19.2570701, lng: -99.5592094 },   // 10. Lázaro Cárdenas
  { lat: 19.2415444, lng: -99.6160446 },   // 11. Zacango
  { lat: 19.2799887, lng: -99.6652454 },   // 12. Carranza
  { lat: 19.306672, lng: -99.5992005 },    // 13. Vicente Lombardo
  { lat: 19.2717938, lng: -99.5540727 },   // 14. Avenida Lerma
  { lat: 19.2557085, lng: -99.5970096 },   // 15. Pedro Ascencio
  { lat: 19.1813924, lng: -99.4712948 },   // 16. Santiago Tianguistenco
  { lat: 19.2517343, lng: -99.6352817 },   // 17. Las Margaritas
  { lat: 19.5703477, lng: -99.7682488 },   // 18. Ixtlahuaca
  { lat: 19.2947013, lng: -99.7219481 },   // 19. Desviación Almoloya de Juárez
  { lat: 19.1851006, lng: -99.5829899 },   // 20. Calimaya
  { lat: 19.2556247, lng: -99.608376 },    // 21. Morelos Metepec
  { lat: 19.2739697, lng: -99.6333032 },   // 22. Valle Don Camilo
  { lat: 19.2853024, lng: -99.4906257 },   // 23. Amomolulco
  { lat: 19.285386, lng: -99.466774 },     // 24. Encinos
  { lat: 19.3592918, lng: -99.5918493 },   // 25. Sauces III
  { lat: 19.2967975, lng: -99.6927159 },   // 26. Niños Héroes
  { lat: 19.3407215, lng: -99.6084484 },   // 27. Santa Cruz
  { lat: 19.2617171, lng: -99.6926328 },   // 28. Alpinismo
  { lat: 19.269203, lng: -99.5964938 },    // 29. Izcalli V
  { lat: 19.321319, lng: -99.6441815 },    // 30. Manuel Buendía
  { lat: 19.2622921, lng: -99.649672 },    // 31. Col. Del Parque
  { lat: 19.2570221, lng: -99.5334809 },   // 32. Garis San Mateo
  { lat: 19.2851698, lng: -99.7132295 },   // 33. Barbabosa
  { lat: 19.2652981, lng: -99.5751751 },   // 34. Paseo de la Asunción
  { lat: 19.2857672, lng: -99.5142965 },   // 35. Centro Lerma
  { lat: 19.2574959, lng: -99.6398485 },   // 36. Alteza
];

function getDistanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function FitBounds({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    map.fitBounds(bounds, { padding: [60, 60], maxZoom: 13 });
  }, [map, bounds]);
  return null;
}

function FlyToBranch({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 16, { duration: 1.2 });
  }, [map, lat, lng]);
  return null;
}

function createBranchIcon(isActive: boolean, isNearest: boolean) {
  const color = isActive ? "#0099aa" : "#00b4c8";
  const size = isActive ? 28 : 20;
  return L.divIcon({
    className: "custom-branch-marker",
    html: `<div style="width:${size}px;height:${size}px;background:${color};border:3px solid white;border-radius:50%;box-shadow:0 2px 10px rgba(0,0,0,0.3);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

function createUserIcon() {
  return L.divIcon({
    className: "custom-user-marker",
    html: `<div style="width:16px;height:16px;background:#7c5cbf;border:3px solid white;border-radius:50%;box-shadow:0 2px 10px rgba(0,0,0,0.3);"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

export default function BranchMap({ branches, activeBranch, onSelectBranch, onNearestFound }: Props) {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [nearestIndex, setNearestIndex] = useState<number | null>(null);
  const [geoStatus, setGeoStatus] = useState<"idle" | "loading" | "success" | "denied">("idle");
  const [distances, setDistances] = useState<number[]>([]);

  const panelRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Scroll automático al panel cuando cambia la sucursal activa
  useEffect(() => {
    if (activeBranch !== null && panelRefs.current[activeBranch]) {
      panelRefs.current[activeBranch]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeBranch]);

  const branchesWithCoords: BranchWithCoords[] = useMemo(
    () => branches.map((b, i) => ({ ...b, ...BRANCH_COORDS[i] })),
    [branches]
  );

  const allBounds = useMemo(() => {
    return L.latLngBounds(BRANCH_COORDS.map((c) => [c.lat, c.lng] as L.LatLngExpression));
  }, []);

  const findNearest = useCallback(
    (userLat: number, userLng: number) => {
      const dists = branchesWithCoords.map((b) =>
        getDistanceKm(userLat, userLng, b.lat, b.lng)
      );
      setDistances(dists);
      const minIdx = dists.indexOf(Math.min(...dists));
      setNearestIndex(minIdx);
      onNearestFound(minIdx);
      onSelectBranch(minIdx);
    },
    [branchesWithCoords, onNearestFound, onSelectBranch]
  );

  const handleLocate = useCallback(() => {
    if (!navigator.geolocation) {
      setGeoStatus("denied");
      return;
    }
    setGeoStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setGeoStatus("success");
        findNearest(latitude, longitude);
      },
      () => {
        setGeoStatus("denied");
      },
      { timeout: 10000 }
    );
  }, [findNearest]);

  const activeCoords =
    activeBranch !== null ? branchesWithCoords[activeBranch] : null;

  return (
    <section className="relative bg-[#fdf5ff]">
      {/* Top bar with locate button */}
      <div className="bg-white border-b border-[#f0e8f5] px-4 md:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-map-pin-2-line text-[#d4607a] text-lg"></i>
          </div>
          <span className="text-sm font-semibold text-[#2d1a0e]">
            {branches.length} sucursales en Edo. Méx. y alrededores
          </span>
          {nearestIndex !== null && geoStatus === "success" && (
            <span className="text-xs bg-[#e0f7fa] text-[#0099aa] px-3 py-1 rounded-full font-medium">
              Más cercana: {branches[nearestIndex].name.replace("San7o Pecado Postrería - ", "")}
            </span>
          )}
        </div>
        <button
          onClick={handleLocate}
          disabled={geoStatus === "loading"}
          className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
            geoStatus === "success"
              ? "bg-[#e8f8f0] text-[#2a9d6e]"
              : geoStatus === "denied"
              ? "bg-[#fff0f3] text-[#d4607a]"
              : "bg-[#00b4c8] text-white hover:bg-[#0099aa]"
          } disabled:opacity-60`}
        >
          {geoStatus === "loading" && (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
          )}
          {geoStatus === "success" && <i className="ri-checkbox-circle-line text-base"></i>}
          {geoStatus === "denied" && <i className="ri-error-warning-line text-base"></i>}
          {geoStatus === "idle" && <i className="ri-crosshair-2-line text-base"></i>}
          {geoStatus === "idle" && "Ubicarme y encontrar la más cercana"}
          {geoStatus === "loading" && "Obteniendo ubicación..."}
          {geoStatus === "success" && "Ubicación encontrada"}
          {geoStatus === "denied" && "Permiso denegado — intenta de nuevo"}
        </button>
      </div>

      <div className="relative flex flex-col lg:flex-row">
        {/* Map */}
        <div className="flex-1 h-[420px] lg:h-[520px] relative z-0">
          <MapContainer
            center={[19.28, -99.65]}
            zoom={11}
            scrollWheelZoom={true}
            style={{ width: "100%", height: "100%" }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {/* Fit all branches when nothing is selected */}
            {activeBranch === null && <FitBounds bounds={allBounds} />}

            {/* Fly to selected branch */}
            {activeCoords && <FlyToBranch lat={activeCoords.lat} lng={activeCoords.lng} />}

            {/* All 35 branch markers */}
            {branchesWithCoords.map((branch, i) => {
              const isActive = activeBranch === i;
              const isNearest = nearestIndex === i;
              return (
                <Marker
                  key={branch.id}
                  position={[branch.lat, branch.lng]}
                  icon={createBranchIcon(isActive, isNearest)}
                  eventHandlers={{
                    click: () => onSelectBranch(i),
                  }}
                >
                  <Popup>
                    <div className="min-w-[220px]">
                      <p className="font-bold text-[#2d1a0e] text-sm mb-1">
                        {branch.name.replace("San7o Pecado Postrería - ", "")}
                      </p>
                      <p className="text-xs text-[#7a5c44] mb-1">{branch.address}</p>
                      <p className="text-xs text-[#9b8aae] mb-2">{branch.hours}</p>
                      {distances[i] !== undefined && (
                        <p className="text-xs font-medium text-[#00b4c8] mb-2">
                          {distances[i] < 1
                            ? `${Math.round(distances[i] * 1000)} m`
                            : `${distances[i].toFixed(1)} km`} de distancia
                        </p>
                      )}
                      <div className="flex flex-col gap-2 mt-2">
                        <a
                          href={`tel:${branch.phone}`}
                          className="text-xs text-[#00b4c8] hover:underline"
                        >
                          {branch.phone}
                        </a>
                        <a
                          href={`https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center gap-1 text-xs font-semibold text-white bg-[#00b4c8] px-3 py-1.5 rounded-full hover:bg-[#0099aa] transition-colors"
                        >
                          <i className="ri-map-pin-line"></i> Cómo Llegar
                        </a>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* User location marker + dashed line to nearest */}
            {userLocation && (
              <>
                <Marker
                  position={[userLocation.lat, userLocation.lng]}
                  icon={createUserIcon()}
                />
                {nearestIndex !== null && (
                  <Polyline
                    positions={[
                      [userLocation.lat, userLocation.lng],
                      [branchesWithCoords[nearestIndex].lat, branchesWithCoords[nearestIndex].lng],
                    ]}
                    pathOptions={{ color: "#00b4c8", weight: 3, dashArray: "6, 6", opacity: 0.8 }}
                  />
                )}
              </>
            )}
          </MapContainer>

          {/* User location badge */}
          {geoStatus === "success" && (
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2.5 flex items-center gap-2 z-[400]">
              <div className="w-2.5 h-2.5 rounded-full bg-[#7c5cbf] animate-pulse"></div>
              <span className="text-xs font-semibold text-[#2d1a0e]">Tu ubicación detectada</span>
            </div>
          )}
        </div>

        {/* Branch Panel */}
        <div className="w-full lg:w-80 bg-white border-t lg:border-t-0 lg:border-l border-[#f0e8f5] overflow-y-auto max-h-[520px]">
          <div className="p-4 border-b border-[#f5e6d3] bg-[#fdf5ff]">
            <h3 className="font-bold text-[#2d1a0e] text-sm">Nuestras Sucursales</h3>
            <p className="text-xs text-[#9b8aae] mt-0.5">Selecciona una para ver en el mapa</p>
          </div>
          {branchesWithCoords.map((branch, i) => {
            const isActive = activeBranch === i;
            const isNearest = nearestIndex === i;
            const dist = distances[i];
            return (
              <button
                key={branch.id}
                ref={(el) => { panelRefs.current[i] = el; }}
                onClick={() => onSelectBranch(i)}
                className={`w-full text-left p-4 border-b border-[#f5e6d3] transition-colors cursor-pointer ${
                  isActive ? "bg-[#e0f7fa]/60" : "hover:bg-[#fdf5ff]"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full mt-0.5 shrink-0 transition-colors ${
                      isNearest && geoStatus === "success"
                        ? "bg-[#00b4c8]/20"
                        : "bg-[#00b4c8]/15"
                    }`}
                  >
                    <i
                      className={`ri-store-2-line text-sm ${
                        isNearest && geoStatus === "success" ? "text-[#00b4c8]" : "text-[#00b4c8]"
                      }`}
                    ></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-[#2d1a0e] text-sm">
                        {branch.name.replace("San7o Pecado Postrería - ", "")}
                      </p>
                      {isNearest && geoStatus === "success" && (
                        <span className="text-[10px] bg-[#00b4c8]/10 text-[#00b4c8] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap">
                          Más cercana
                        </span>
                      )}
                    </div>
                    <p className="text-[#7a5c44] text-xs mt-0.5 leading-relaxed">{branch.address}</p>
                    <p className="text-[#9b8aae] text-xs mt-0.5">{branch.hours}</p>
                    {dist !== undefined && (
                      <p className="text-[#00b4c8] text-xs font-medium mt-1">
                        <i className="ri-walk-line mr-1"></i>
                        {dist < 1 ? `${Math.round(dist * 1000)} m` : `${dist.toFixed(1)} km`} de distancia
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <a
                        href={`tel:${branch.phone}`}
                        className="text-[#00b4c8] text-xs font-medium hover:underline whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="ri-phone-line mr-1"></i>{branch.phone}
                      </a>
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${branch.lat},${branch.lng}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#00b4c8] px-3 py-1 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <i className="ri-map-pin-line"></i> Cómo Llegar
                      </a>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}